// src/services/chatLists.js — the chat-list filters, the app's own collection.
//
//   users/{uid}/chatLists/{listId} = { name, chatIds: [chatId], createdAt }
//
// That is exactly where ChatsTab reads and writes them, so a list made here
// appears on the phone and vice versa, with no extra plumbing — it's one
// subcollection under my own user document.
//
// The fixed filters (All / Unread / Groups) are not documents on either side:
// they're computed from the chats themselves, so they need no storage and can
// never fall out of sync.
//
// 🟥 AN EMPTY LIST DELETES ITSELF. The app does this (ChatsTab.handleRemoveMember:
//    removing the last member deletes the doc rather than leaving a list that can
//    never match anything), so the web has to as well — otherwise the two clients
//    disagree about whether a list still exists.

import {
  collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot,
  arrayUnion, arrayRemove, serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';

const col = (uid) => collection(db, 'users', uid, 'chatLists');
const ref = (uid, id) => doc(db, 'users', uid, 'chatLists', id);

/** Live list of my custom lists, oldest first (the order the app shows them). */
export function watchChatLists(uid, cb) {
  if (!uid) { cb([]); return () => {}; }
  // orderBy is best-effort: lists written before createdAt existed still show
  try {
    return onSnapshot(query(col(uid), orderBy('createdAt', 'asc')),
      (s) => cb(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => onSnapshot(col(uid), (s2) => cb(s2.docs.map((d) => ({ id: d.id, ...d.data() })))));
  } catch {
    return onSnapshot(col(uid), (s) => cb(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => cb([]));
  }
}

export async function createList(uid, name, chatIds = []) {
  const clean = String(name || '').trim();
  if (!clean) throw new Error('name required');
  const d = await addDoc(col(uid), {
    name: clean,
    chatIds: [...new Set(chatIds.filter(Boolean))],
    createdAt: serverTimestamp(),
  });
  return d.id;
}

export const renameList = (uid, id, name) =>
  updateDoc(ref(uid, id), { name: String(name || '').trim() });

export const addToList = (uid, id, chatId) =>
  updateDoc(ref(uid, id), { chatIds: arrayUnion(chatId) });

/** Removing the last member deletes the list — the app's rule. */
export async function removeFromList(uid, id, chatId, currentIds = []) {
  const left = currentIds.filter((c) => c !== chatId);
  if (left.length === 0) { await deleteDoc(ref(uid, id)); return 'deleted'; }
  await updateDoc(ref(uid, id), { chatIds: arrayRemove(chatId) });
  return 'removed';
}

export const deleteList = (uid, id) => deleteDoc(ref(uid, id));

/** The deterministic 1:1 chat id, so a person can join a list before you've
 *  ever spoken — the same id App.startChat would create. */
export const chatIdFor = (a, b) => [a, b].sort().join('_');

/**
 * Apply a filter to the chat list.
 * @param filter  'all' | 'unread' | 'groups' | a list id
 */
export function applyFilter(chats, filter, lists, hasUnread) {
  if (!filter || filter === 'all') return chats;
  if (filter === 'unread') return chats.filter((c) => hasUnread(c));
  if (filter === 'groups') return chats.filter((c) => c.isGroup);
  const list = (lists || []).find((l) => l.id === filter);
  if (!list) return chats;
  const ids = new Set(list.chatIds || []);
  return chats.filter((c) => ids.has(c.id));
}
